import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchNegotiationDetail,
  fetchMessages,
  sendMessage,
  makeOffer,
  acceptOffer,
  rejectOffer,
  clearCurrentNegotiation,
  fetchUnreadNotificationsCount,
} from '../../store/slices/negotiationsSlice';
import { fetchCart } from '../../store/slices/cartSlice';
import { negotiationsAPI } from '../../api/negotiationsAPI';
import { fetchItemById } from '../../store/slices/itemsSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import {
  getNegotiationDisplayLabel,
  getNegotiationItemCode,
} from '../../utils/negotiationDisplay';
import customizationsAPI, { getCustomizationRequestId } from '../../api/customizationsAPI';
import PaintingBriefView from '../../components/customizations/PaintingBriefView';

const DetailContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const DetailCard = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2rem;
`;

const StatusBadge = styled.span`
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: ${props => 
    props.status === 'ACTIVE' ? '#e3f2fd' :
    props.status === 'COMPLETED' ? '#e8f5e9' :
    props.status === 'REJECTED' ? '#ffebee' : '#f5f5f5'};
  color: ${props => 
    props.status === 'ACTIVE' ? '#1976d2' :
    props.status === 'COMPLETED' ? '#388e3c' :
    props.status === 'REJECTED' ? '#d32f2f' : '#757575'};
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const SectionHint = styled.p`
  margin: 0 0 1rem;
  color: var(--text-secondary);
  font-size: 0.95rem;
`;

const OfferMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--primary-color);
  color: white;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: var(--text-primary);
  border: 2px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--bg-secondary);
  }
`;

const OfferForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background-color: var(--bg-secondary);
  border-radius: 0.5rem;
  margin-top: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
`;

const OffersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OfferCard = styled.div`
  padding: 1rem;
  background-color: var(--bg-secondary);
  border-radius: 0.5rem;
  border-left: 4px solid ${props => 
    props.status === 'PENDING' ? '#ff9800' :
    props.status === 'ACCEPTED' ? '#4caf50' :
    '#f44336'};
`;

const OfferHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const OfferAmount = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PinnedBrief = styled.section`
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  margin-bottom: 1rem;
  padding: 1rem 1.25rem;
  position: sticky;
  top: 0.75rem;
  z-index: 5;
`;

const PinnedBriefHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
`;

const BriefSummary = styled.p`
  color: var(--text-secondary);
  margin: 0.35rem 0 0;
  text-transform: capitalize;
`;

const BriefButton = styled.button`
  background: transparent;
  border: 0;
  color: var(--primary-color, #1b4332);
  cursor: pointer;
  font-weight: 700;
  margin-top: 0.75rem;
  padding: 0;
`;

const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const MessageBubble = styled.div`
  align-self: ${(props) => props.$mine ? 'flex-end' : 'flex-start'};
  background: ${(props) => props.$mine ? 'var(--primary-color, #1b4332)' : 'var(--bg-secondary, #f1f5f9)'};
  border-radius: 0.75rem;
  color: ${(props) => props.$mine ? '#fff' : 'var(--text-primary)'};
  max-width: min(75%, 620px);
  padding: 0.75rem 0.9rem;
`;

const MessageText = styled.p`
  line-height: 1.5;
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const MessageDate = styled.div`
  font-size: 0.7rem;
  margin-top: 0.35rem;
  opacity: 0.72;
`;

const MessageComposer = styled.form`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const MessageInput = styled.textarea`
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  box-sizing: border-box;
  flex: 1;
  font: inherit;
  min-height: 84px;
  padding: 0.75rem;
  resize: vertical;
`;

const NegotiationDetailPage = () => {
  const { negotiationRef } = useParams();
  const id = negotiationsAPI.normalizeNegotiationId(negotiationRef);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentNegotiation, messages, loading, error } = useSelector((state) => state.negotiations);
  const { user } = useSelector((state) => state.auth);
  const { currentItem } = useSelector((state) => state.items);
  
  const [offerAmount, setOfferAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [customizationRequest, setCustomizationRequest] = useState(null);
  const [briefExpanded, setBriefExpanded] = useState(false);
  const customizationRequestId = getCustomizationRequestId(currentNegotiation);

  useEffect(() => {
    if (id && user) {
      dispatch(fetchNegotiationDetail(id));
      dispatch(fetchMessages(id));
    }
    
    return () => {
      dispatch(clearCurrentNegotiation());
    };
  }, [dispatch, id, user]);

  useEffect(() => {
    if (!customizationRequestId) {
      setCustomizationRequest(null);
      return;
    }
    customizationsAPI
      .getBuyerRequest(customizationRequestId)
      .then(setCustomizationRequest)
      .catch(() => setCustomizationRequest(null));
  }, [customizationRequestId]);

  // Opening a completed negotiation marks notifications read server-side — refresh badge.
  useEffect(() => {
    if (currentNegotiation?.status === 'COMPLETED' && user) {
      dispatch(fetchUnreadNotificationsCount());
    }
  }, [dispatch, currentNegotiation?.status, currentNegotiation?.id, user]);

  useEffect(() => {
    if (currentNegotiation?.item_id && !currentItem) {
      dispatch(fetchItemById(currentNegotiation.item_id));
    }
  }, [dispatch, currentNegotiation, currentItem]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'An error occurred');
    }
  }, [error]);

  const handleMakeOffer = async (e) => {
    e.preventDefault();
    const amount = parseFloat(offerAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await dispatch(makeOffer({ id, pointsAmount: amount })).unwrap();
      setOfferAmount('');
      toast.success('Offer submitted!');
      dispatch(fetchNegotiationDetail(id));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to make offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptOffer = async () => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(acceptOffer(id)).unwrap();
      const isBuyer = user?.id === currentNegotiation?.buyer_id || user?.id === result?.buyer_id;
      dispatch(fetchNegotiationDetail(id));
      if (isBuyer && result?.added_to_cart) {
        dispatch(fetchCart());
        toast.success(
          result.cart_message
            || 'Item successfully Added To Cart! Checkout and pay so it can be fully yours before someone else picks it!',
          { autoClose: 8000 }
        );
        navigate('/cart');
      } else if (isBuyer) {
        toast.warn(
          'Offer accepted. Please add the item to your cart and checkout before someone else picks it!',
          { autoClose: 8000 }
        );
      } else {
        toast.success('Offer accepted! The buyer can now checkout from their cart.');
      }
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to accept offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectOffer = async () => {
    setIsSubmitting(true);
    try {
      await dispatch(rejectOffer(id)).unwrap();
      toast.success('Offer rejected');
      dispatch(fetchNegotiationDetail(id));
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to reject offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    const content = messageText.trim();
    if (!content) return;
    setSendingMessage(true);
    try {
      await dispatch(sendMessage({ id, content })).unwrap();
      setMessageText('');
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading && !currentNegotiation) {
    return <LoadingSpinner text="Loading negotiation details..." />;
  }

  if (!currentNegotiation) {
    return (
      <DetailContainer>
        <DetailCard>
          <p>Negotiation not found</p>
        </DetailCard>
      </DetailContainer>
    );
  }

  const displayLabel = getNegotiationDisplayLabel(currentNegotiation, currentItem);
  const itemCode = getNegotiationItemCode(currentNegotiation, currentItem);
  const pendingOffer = currentNegotiation.offers?.find(o => o.status === 'PENDING');
  const isCustomization = Boolean(getCustomizationRequestId(currentNegotiation));
  const canMakeOffer = !isCustomization && currentNegotiation.status === 'ACTIVE' && !pendingOffer;

  return (
    <DetailContainer>
      {customizationRequest ? (
        <PinnedBrief>
          <PinnedBriefHeader>
            <div>
              <SectionTitle style={{ margin: 0 }}>Custom painting brief</SectionTitle>
              <BriefSummary>
                {customizationRequest.painting_brief?.width_cm || '—'} ×{' '}
                {customizationRequest.painting_brief?.height_cm || '—'} cm ·{' '}
                {String(customizationRequest.painting_brief?.creative_direction || '').replace(/_/g, ' ')}
              </BriefSummary>
            </div>
            <StatusBadge status={currentNegotiation.status}>
              {customizationRequest.state}
            </StatusBadge>
          </PinnedBriefHeader>
          {briefExpanded ? (
            <div style={{ marginTop: '1rem' }}>
              <PaintingBriefView request={customizationRequest} />
            </div>
          ) : null}
          <BriefButton type="button" onClick={() => setBriefExpanded((value) => !value)}>
            {briefExpanded ? 'Hide brief' : 'View brief'}
          </BriefButton>
        </PinnedBrief>
      ) : null}
      <DetailCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <PageTitle style={{ marginBottom: itemCode ? '0.5rem' : '2rem' }}>
              {displayLabel}
            </PageTitle>
            {itemCode ? (
              <p style={{ color: 'var(--text-secondary)', fontFamily: 'monospace', margin: 0 }}>
                {itemCode}
              </p>
            ) : null}
          </div>
          <StatusBadge status={currentNegotiation.status}>{currentNegotiation.status}</StatusBadge>
        </div>

        {!isCustomization ? <Section>
          <SectionTitle>Price offers</SectionTitle>
          <SectionHint>
            Negotiations are price-only — submit a number in EGP, accept, reject, or counter with a new amount.
          </SectionHint>
          {currentNegotiation.offers && currentNegotiation.offers.length > 0 ? (
            <OffersList>
              {currentNegotiation.offers.map((offer) => (
                <OfferCard key={offer.id} status={offer.status}>
                  <OfferHeader>
                    <OfferAmount>{formatItemPriceEgp(offer.points_amount)}</OfferAmount>
                    <StatusBadge status={offer.status}>{offer.status}</StatusBadge>
                  </OfferHeader>
                  <OfferMeta>
                    {new Date(offer.created_at).toLocaleString()}
                  </OfferMeta>
                  {offer.status === 'PENDING' && offer.offered_by_id !== user?.id && (
                    <ActionButtons>
                      <PrimaryButton
                        onClick={handleAcceptOffer}
                        disabled={isSubmitting}
                      >
                        Accept Offer
                      </PrimaryButton>
                      <SecondaryButton
                        onClick={handleRejectOffer}
                        disabled={isSubmitting}
                      >
                        Reject Offer
                      </SecondaryButton>
                    </ActionButtons>
                  )}
                </OfferCard>
              ))}
            </OffersList>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }}>No offers yet</p>
          )}

          {canMakeOffer && (
            <OfferForm onSubmit={handleMakeOffer}>
              <FormGroup>
                <Label>Your offer (EGP)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={offerAmount}
                  onChange={(e) => setOfferAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                  placeholder="Enter amount in EGP"
                  min="1"
                  step="0.01"
                  required
                />
              </FormGroup>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting…' : 'Submit offer'}
              </PrimaryButton>
            </OfferForm>
          )}
        </Section> : null}

        <Section>
          <SectionTitle>Conversation</SectionTitle>
          {messages?.length ? (
            <MessagesList>
              {messages.map((message) => (
                <MessageBubble key={message.id} $mine={message.sender_id === user?.id}>
                  <MessageText>{message.content}</MessageText>
                  <MessageDate>{new Date(message.created_at).toLocaleString()}</MessageDate>
                </MessageBubble>
              ))}
            </MessagesList>
          ) : (
            <div>
              <strong>Start the conversation</strong>
              <SectionHint>
                {isCustomization
                  ? 'Ask a question or clarify a detail about the painting.'
                  : 'Send a message about this negotiation.'}
              </SectionHint>
            </div>
          )}
          {currentNegotiation.status === 'ACTIVE' ? (
            <MessageComposer onSubmit={handleSendMessage}>
              <MessageInput
                aria-label="Write a message"
                value={messageText}
                onChange={(event) => setMessageText(event.target.value)}
                placeholder="Write a message…"
                disabled={sendingMessage}
              />
              <PrimaryButton type="submit" disabled={sendingMessage || !messageText.trim()}>
                {sendingMessage ? 'Sending…' : 'Send'}
              </PrimaryButton>
            </MessageComposer>
          ) : isCustomization ? (
            <SectionHint>
              This customization request is closed. Previous messages are still available.
            </SectionHint>
          ) : null}
        </Section>
      </DetailCard>
    </DetailContainer>
  );
};

export default NegotiationDetailPage;
